import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Language as LanguageIcon,
  Home as HomeIcon,
  FlightTakeoff as FlightIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import WeChatQRDialog from '../common/WeChatQRDialog';
import useWeChatQR from '../../hooks/useWeChatQR';

const ServicesOverview = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openQR, openWeChatQR, closeWeChatQR } = useWeChatQR();

  const services = [
    {
      icon: <SchoolIcon sx={{ fontSize: 40 }} />,
      title: '院校申请',
      description: '专业的院校选择指导，帮您找到最适合的大学和专业，提高申请成功率。',
      features: ['个性化院校推荐', '申请材料准备', '面试辅导'],
      color: theme.palette.primary.main,
    },
    {
      icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
      title: '签证服务',
      description: '全程签证申请指导，确保材料完整准确，提高签证通过率。',
      features: ['签证材料清单', '表格填写指导', '面签培训'],
      color: theme.palette.secondary.main,
    },
    {
      icon: <LanguageIcon sx={{ fontSize: 40 }} />,
      title: '语言培训',
      description: '专业的雅思、托福培训课程，快速提升语言水平。',
      features: ['一对一辅导', '模拟考试', '写作批改'],
      color: theme.palette.success.main,
    },
    {
      icon: <HomeIcon sx={{ fontSize: 40 }} />,
      title: '住宿安排',
      description: '提供多样化住宿选择，确保您的留学生活舒适安全。',
      features: ['校内宿舍', '校外公寓', '寄宿家庭'],
      color: theme.palette.warning.main,
    },
    {
      icon: <FlightIcon sx={{ fontSize: 40 }} />,
      title: '接机服务',
      description: '贴心的接机和初期生活安排，让您快速适应新环境。',
      features: ['机场接送', '开户指导', '购物陪同'],
      color: theme.palette.info.main,
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40 }} />,
      title: '全程支持',
      description: '留学期间持续的学术和生活支持，确保顺利完成学业。',
      features: ['学业辅导', '生活咨询', '紧急协助'],
      color: theme.palette.error.main,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box
      sx={{
        py: 8,
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.02)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      }}
    >
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: theme.palette.text.primary,
              }}
            >
              专业留学服务
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              从申请到毕业，我们为您提供全方位的留学支持服务
            </Typography>
          </Box>

          {/* Services Grid */}
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <motion.div variants={cardVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: `2px solid ${alpha(service.color, 0.1)}`,
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        borderColor: alpha(service.color, 0.3),
                        boxShadow: `0 16px 40px ${alpha(service.color, 0.15)}`,
                        '& .service-icon': {
                          transform: 'scale(1.1) rotate(5deg)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      {/* Service Icon */}
                      <Box
                        className="service-icon"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 80,
                          height: 80,
                          borderRadius: 3,
                          backgroundColor: alpha(service.color, 0.1),
                          color: service.color,
                          mb: 3,
                          transition: 'transform 0.3s ease',
                        }}
                      >
                        {service.icon}
                      </Box>

                      {/* Service Title */}
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 2,
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {service.title}
                      </Typography>

                      {/* Service Description */}
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 3,
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {service.description}
                      </Typography>

                      {/* Service Features */}
                      <Box sx={{ mb: 3 }}>
                        {service.features.map((feature, featureIndex) => (
                          <Box
                            key={featureIndex}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: service.color,
                                mr: 2,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* CTA Button */}
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={openWeChatQR}
                        sx={{
                          borderColor: service.color,
                          color: service.color,
                          fontWeight: 600,
                          py: 1.5,
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: alpha(service.color, 0.1),
                            borderColor: service.color,
                          },
                        }}
                      >
                        了解更多
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* CTA Section */}
          <Box
            sx={{
              textAlign: 'center',
              mt: 8,
              p: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              准备开始您的留学之旅？
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: theme.palette.text.secondary,
              }}
            >
              联系我们的专业顾问，获取个性化的留学规划方案
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={openWeChatQR}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 25,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                立即咨询
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/services')}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 25,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                在线评估
              </Button>
            </Box>
          </Box>
        </motion.div>
      </Container>

      <WeChatQRDialog 
        open={openQR} 
        onClose={closeWeChatQR}
        title="立即咨询"
      />
    </Box>
  );
};

export default ServicesOverview;
